// Hand, Thumb & Fingers Template Configuration
// Questions simplified to 12-year-old reading level

export const handTemplate = {
  id: 'HAND',
  name: 'Hand, Thumb & Fingers',
  icon: '✋',
  dominantSide: 'hand',

  fields: {
    dominantHand: {
      type: 'radio',
      label: 'Which is your dominant hand (the one you write with)?',
      options: ['Right', 'Left', 'Ambidextrous'],
      required: true
    },

    injuredSide: {
      type: 'radio',
      label: 'Which hand has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuredDigit: {
      type: 'checkbox-multiple',
      label: 'Which finger or part of the hand is injured? (tick all that apply)',
      options: [
        'Thumb',
        'Index finger',
        'Middle finger',
        'Ring finger',
        'Little finger',
        'Multiple fingers',
        'Whole hand/not specific'
      ],
      required: true
    },

    injuredJoint: {
      type: 'checkbox-multiple',
      label: 'Which joint or part is affected? (tick all that apply)',
      options: [
        'Tip joint (DIP)',
        'Middle joint (PIP)',
        'Knuckle (MCP)',
        'Tip joint of thumb (IP)',
        'Base of thumb (MCP/CMC)',
        'Bone in back of hand (metacarpal)',
        'Not sure'
      ]
    },

    injuryDate: {
      type: 'date',
      label: 'When did you injure your hand?',
      required: true
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Describe how the injury happened:',
      placeholder: 'What happened when you injured your hand?',
      rows: 3,
      required: true
    },

    // Mechanism Section
    mechanism: {
      type: 'checkbox-multiple',
      label: 'How did you injure your hand? (tick all that apply)',
      options: [
        'Jammed finger (caught ball/hit wall)',
        'Bent backwards (hyperextension)',
        'Bent sideways',
        'Caught in clothing/jersey',
        'Punched something (boxer\'s fracture)',
        'Fall onto outstretched hand',
        'Crush injury (door/weight)',
        'Cut or laceration',
        'Bite (animal or human)',
        'Fell skiing — pole caught thumb (skier\'s thumb)',
        'Other'
      ]
    },

    mechanismOther: {
      type: 'text',
      label: 'Please describe what happened:',
      placeholder: 'Tell us how you were injured',
      showWhen: { field: 'mechanism', includes: 'Other' }
    },

    // Pain & Function Section
    painLocation: {
      type: 'text',
      label: 'Point to exactly where it hurts:',
      placeholder: 'e.g., tip of ring finger, base of thumb, back of hand'
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Sharp', 'Aching', 'Throbbing', 'Burning', 'Other']
    },

    painCharacterOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painCharacter', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    swelling: {
      type: 'radio',
      label: 'Is there swelling?',
      options: ['Yes', 'No']
    },

    bruising: {
      type: 'radio',
      label: 'Is there bruising?',
      options: ['Yes', 'No']
    },

    deformity: {
      type: 'radio',
      label: 'Does anything look out of shape or crooked?',
      options: ['Yes, finger looks crooked', 'Yes, drooping fingertip', 'No']
    },

    cantStraightenFingertip: {
      type: 'radio',
      label: 'Can you straighten the tip of your finger? (mallet finger)',
      options: ['Yes', 'No', 'Not applicable']
    },

    cantBendFingertip: {
      type: 'radio',
      label: 'Can you bend the tip of your finger? (jersey finger/FDP)',
      options: ['Yes', 'No', 'Not applicable']
    },

    cantStraightenMiddleJoint: {
      type: 'radio',
      label: 'Can you straighten the middle joint of your finger? (boutonniere)',
      options: ['Yes', 'No', 'Not applicable']
    },

    unstableJoint: {
      type: 'radio',
      label: 'Does any joint feel loose or wobbly?',
      options: ['Yes', 'No']
    },

    unableToGrip: {
      type: 'radio',
      label: 'Can you grip things?',
      options: ['Yes', 'No']
    },

    unableToPinch: {
      type: 'radio',
      label: 'Can you pinch between your thumb and finger? (thumb UCL)',
      options: ['Yes', 'No']
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Gripping things',
        'Pinching',
        'Writing',
        'Typing',
        'Lifting',
        'Twisting (like opening a jar)',
        'Bending the finger',
        'Straightening the finger',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Ice',
        'Heat',
        'Buddy taping',
        'Splint',
        'Pain medication',
        'Anti-inflammatory medication',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Tendon Checks
    canMakeFist: {
      type: 'radio',
      label: 'Can you make a full fist?',
      options: ['Yes, full fist', 'Partly', 'No']
    },

    canTouchThumbToLittleFinger: {
      type: 'radio',
      label: 'Can you touch your thumb to your little finger?',
      options: ['Yes', 'No']
    },

    canStraightenAllFingers: {
      type: 'radio',
      label: 'Can you straighten all your fingers fully?',
      options: ['Yes', 'No']
    },

    // Sport/Occupation Context
    sport: {
      type: 'text',
      label: 'What sports or activities do you do?',
      placeholder: 'Tell us about your sports and how active you are'
    },

    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    playingPosition: {
      type: 'text',
      label: 'What position do you play?',
      placeholder: 'e.g., goalkeeper, prop, wicketkeeper'
    },

    workRequiresGrip: {
      type: 'radio',
      label: 'Does your work require a lot of gripping or hand use?',
      options: ['Yes', 'No']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Hand Injury',
      component: 'GenericSection',
      enabled: true,
      fields: ['dominantHand', 'injuredSide', 'injuredDigit', 'injuredJoint', 'injuryDate', 'injuryDescription']
    },
    {
      id: 'mechanism',
      title: '3. How It Happened',
      component: 'GenericSection',
      enabled: true,
      fields: ['mechanism', 'mechanismOther']
    },
    {
      id: 'pain',
      title: '4. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painCharacter', 'painCharacterOther', 'painSeverity', 'swelling', 'bruising', 'deformity', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'function',
      title: '5. How Your Hand Works',
      component: 'GenericSection',
      enabled: true,
      fields: ['cantStraightenFingertip', 'cantBendFingertip', 'cantStraightenMiddleJoint', 'unstableJoint', 'unableToGrip', 'unableToPinch', 'canMakeFist', 'canTouchThumbToLittleFinger', 'canStraightenAllFingers']
    },
    {
      id: 'redFlags',
      title: '6. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        { id: 'openWound', label: 'Is there an open wound?', weight: 8, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'fingerColourChange', label: 'Has the finger changed colour (gone white or blue)?', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'completeNumbness', label: 'Is the finger completely numb (no feeling at all)?', weight: 6, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'severeDeformity', label: 'Is the finger or hand severely out of shape?', weight: 6, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'biteWound', label: 'Was this caused by an animal or human bite?', weight: 7, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'fightBite', label: 'Did you cut your knuckle on someone\'s teeth (fight bite)?', weight: 8, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'highPressureInjection', label: 'Was this a high-pressure injection injury (paint gun, grease gun)?', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'spreadingRedness', label: 'Is there spreading redness, warmth, or pus (signs of infection)?', weight: 6, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'crushInjury', label: 'Was the hand crushed (e.g., in a door or by a heavy object)?', weight: 5, type: 'radio', options: ['Yes', 'No'] }
      ],
      alerts: {
        critical: { threshold: 10, message: 'URGENT: These symptoms need immediate medical attention' },
        urgent: { threshold: 7, message: 'Please contact us promptly about these symptoms' },
        concerning: { threshold: 5, message: 'Please mention these symptoms at your appointment' }
      }
    },
    {
      id: 'context',
      title: '7. Sport & Work',
      component: 'GenericSection',
      enabled: true,
      fields: ['sport', 'occupation', 'playingPosition', 'workRequiresGrip']
    },
    {
      id: 'medical',
      title: '8. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'exerciseVitalSign',
      title: '9. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },
    {
      id: 'goals',
      title: '10. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Jammed finger (caught ball/hit wall)': 'a jammed finger injury',
      'Bent backwards (hyperextension)': 'hyperextension of the digit',
      'Bent sideways': 'lateral deviation force to the digit',
      'Caught in clothing/jersey': 'finger caught in clothing (jersey finger mechanism)',
      'Punched something (boxer\'s fracture)': 'punching injury (boxer\'s fracture mechanism)',
      'Fall onto outstretched hand': 'fall onto outstretched hand (FOOSH)',
      'Crush injury (door/weight)': 'a crush injury',
      'Cut or laceration': 'a laceration',
      'Bite (animal or human)': 'a bite injury',
      'Fell skiing — pole caught thumb (skier\'s thumb)': 'forced thumb abduction (skier\'s thumb mechanism)'
    },

    commonDiagnoses: [
      'Mallet finger (DIP extensor avulsion)',
      'Jersey finger (FDP avulsion)',
      'Boutonniere deformity (central slip)',
      'PIP collateral ligament sprain',
      'PIP dorsal dislocation',
      'Volar plate injury',
      'Thumb UCL (skier\'s/gamekeeper\'s — Stener lesion)',
      'Boxer\'s fracture (5th MC neck)',
      'Metacarpal shaft fracture',
      'Phalangeal fracture',
      'Scaphoid fracture (consider wrist template)',
      'Flexor tendon injury/laceration',
      'Extensor tendon injury/laceration',
      'Trigger finger',
      'Fight bite injury (surgical emergency)',
      'High-pressure injection injury (surgical emergency)'
    ]
  }
};

export default handTemplate;
